<script lang="ts" setup>
import { useAutoRoundBoost } from "@/composable/chat/useAutoRoundBoost";
import { InputTypeEnum } from "@/enums/chatEnums";
import { useAudio } from "@/stores/audio";
import { useChatSession } from "@/stores/chatSession";
import { MessageListInstance } from "@/types/component";
import { ChatMessage, Mate } from "@/types/entity";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { ChatFooterInstance } from "../common/ChatTextFooter.vue";

const props = withDefaults(
  defineProps<{
    mate?: Mate;
    mateSessionId: number;
    shouldLogin?: boolean;
    mateImageList?: MateMedia[];
  }>(),
  {
    shouldLogin: false,
    mateImageList: () => [],
  }
);

// 状态
const route = useRoute();
const sessionStore = useChatSession();
const audioStore = useAudio();
const mateSessionId = Number(route.params.mateSessionId);
const currentSession = computed(
  () => sessionStore.sessionMap[props.mateSessionId]
);
const isPrivate = computed(() => {
  if (!currentSession.value) {
    return false;
  }
  return currentSession.value?.isPrivate === 1;
});
const messageListRef = ref<MessageListInstance | null>(null);
const myMessage = ref<string>("");
const isStreamReplying = ref(false);
const streamMsg = ref<string>("");
const isPostingMsg = ref<"start" | "end" | false>(false);

const chatTextFooterRef = ref<ChatFooterInstance | null>(null);

const postMessage = computed(() => chatTextFooterRef.value?.postMessage);
const isSceneIncludeTarget = computed(() => {
  return !!currentSession.value?.chatScene?.characterzz?.find(
    (item) => item.targetzz?.length
  );
});
const myMsgToNames = ref<string[]>([]);
useAutoRoundBoost(
  mateSessionId,
  computed(() => !!isPostingMsg.value),
  postMessage,
  isSceneIncludeTarget
);

const streamingNames = computed(() => {
  if (myMsgToNames.value.length > 0) {
    return myMsgToNames.value;
  }
  return props.mate?.name ? [props.mate?.name] : [];
});
// Event Handler

const onStreamFinish = (data: {
  userMsg?: ChatMessage;
  assistantMsg?: ChatMessage;
}) => {
  if (audioStore.enableAutoPlay && data.assistantMsg) {
    messageListRef.value?.playAudioMsg?.(data.assistantMsg.id);
  }
};

const onContinueBtnClick = (targets: string[]) => {
  chatTextFooterRef.value?.postMessage({
    inputType: InputTypeEnum.CONTINUE,
    text: "",
    toNames: targets,
    loadingPosition: "start",
  });
};

const enableAvatar = ref(true);
</script>
<template>
  <div class="h-full grid grid-rows-[1fr_auto_auto] overflow-hidden">
    <ClassicMessageList
      v-if="!isSceneIncludeTarget"
      ref="messageListRef"
      :session-id="mateSessionId"
      :mate="mate"
      :my-message="myMessage"
      :stream-msg="streamMsg"
      :is-stream-replying="isStreamReplying"
      :is-posting-msg="isPostingMsg"
      @on-continue-btn-click="onContinueBtnClick"
    />
    <RetroMessageList
      v-else
      ref="messageListRef"
      :session-id="mateSessionId"
      :mate="mate"
      :my-message="myMessage"
      :stream-msg="streamMsg"
      :is-stream-replying="isStreamReplying"
      :is-posting-msg="isPostingMsg"
      :streaming-names="streamingNames"
      :enable-avatar="enableAvatar"
      @on-continue-btn-click="onContinueBtnClick"
    />
    <ChatTextFooter
      ref="chatTextFooterRef"
      v-model:enable-avatar="enableAvatar"
      v-model:is-posting-msg="isPostingMsg"
      v-model:is-stream-replying="isStreamReplying"
      v-model:my-message="myMessage"
      v-model:stream-msg="streamMsg"
      :currentSession="currentSession"
      :enable-history-mode="false"
      :is-private="isPrivate"
      :mate="mate!"
      :mate-session-id="mateSessionId"
      :should-login="shouldLogin"
      @onInputFocus="messageListRef?.scrollToBottom(false)"
      @on-stream-finish="onStreamFinish"
      @on-post-msg-click="myMsgToNames = $event ?? []"
    />
  </div>
</template>
